import { Check, X } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { useAgentName } from './agentName'

/** Stands for every job at once, so it cannot be held alongside a specific one. */
const GENERAL_ROLE = 'General (All)'

/** The jobs people actually hold at a fleet this size. */
const ROLES = [
  GENERAL_ROLE,
  'Fleet owner',
  'Operations',
  'Dispatch',
  'Safety',
  'Driver',
  'Accounting/Bookkeeping',
  'Maintenance',
]

/** What they may change, as opposed to what they do. Most invites are not admins. */
const ACCESS = ['Admin', 'Member']

/**
 * What this person can act on. Several at once, because one person at a small
 * fleet covers several — the four Cai capabilities plus the fleet's own areas.
 */
const RESPONSIBILITIES = ['Insurance', 'Claims', 'Compliance', 'Drivers', 'Equipment', 'Books', 'Fuel']

interface ChipGroupProps {
  label: string
  options: string[]
  selected: string[]
  onToggle: (option: string) => void
  hint?: string
}

/** A field whose answer can be more than one thing. */
function ChipGroup({ label, options, selected, onToggle, hint }: ChipGroupProps) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-ink-500 text-[10px]">{label}</span>
      <div className="flex flex-wrap gap-1">
        {options.map((option) => {
          const on = selected.includes(option)
          return (
            <Button
              key={option}
              type="button"
              size="xs"
              variant="outline"
              aria-pressed={on}
              onClick={() => onToggle(option)}
              className={cn('text-[10px]', on && 'border-brand-700 bg-brand-100')}
            >
              {on && <Check className="size-3" />}
              {option}
            </Button>
          )
        })}
      </div>
      {hint && <span className="text-ink-400 text-[10px] leading-snug">{hint}</span>}
    </div>
  )
}

/**
 * Adding someone, raised over the Connect tab it started from. A sheet rather
 * than a screen of its own: the list you are adding to stays behind it, so it
 * reads as one step rather than somewhere else you went.
 */
export function InviteSheet({ onClose }: { onClose: () => void }) {
  const agentName = useAgentName()
  const [roles, setRoles] = useState<string[]>([])
  const [areas, setAreas] = useState<string[]>([])

  function toggleRole(role: string) {
    // General stands for all of them, so it and a specific job cancel each other.
    if (role === GENERAL_ROLE) {
      setRoles((was) => (was.includes(GENERAL_ROLE) ? [] : [GENERAL_ROLE]))
      return
    }
    setRoles((was) => {
      const without = was.filter((r) => r !== GENERAL_ROLE)
      return without.includes(role) ? without.filter((r) => r !== role) : [...without, role]
    })
  }

  const toggleArea = (area: string) =>
    setAreas((was) => (was.includes(area) ? was.filter((a) => a !== area) : [...was, area]))

  return (
    <div className="absolute inset-0 flex flex-col justify-end">
      {/* Tapping away from the sheet is the other way out of it. */}
      <button type="button" aria-label="Dismiss" onClick={onClose} className="bg-ink/20 absolute inset-0" />
      {/* Scrolls rather than growing past the top of the phone. */}
      <div className="no-scrollbar border-ink-100 relative flex max-h-full flex-col gap-3 overflow-y-auto rounded-t-xl border-t bg-white p-3 shadow-xl">
        <div className="flex items-center gap-2">
          <span className="text-ink flex-1 text-sm font-semibold">Invite someone</span>
          <button type="button" aria-label="Close" onClick={onClose} className="hover:bg-ink-50 rounded p-1">
            <X className="text-ink-300 size-4" />
          </button>
        </div>

        <label className="flex flex-col gap-1">
          <span className="text-ink-500 text-[10px]">Email</span>
          <Input size="sm" placeholder="name@blueline.com" className="text-xs" />
        </label>

        <ChipGroup label="Role" options={ROLES} selected={roles} onToggle={toggleRole} />

        <label className="flex flex-col gap-1">
          <span className="text-ink-500 text-[10px]">Access</span>
          {/* Unset like the rest: the form should not answer for you. */}
          <Select>
            <SelectTrigger size="sm" className="w-full text-xs">
              <SelectValue placeholder="Choose access" />
            </SelectTrigger>
            <SelectContent>
              {ACCESS.map((level) => (
                <SelectItem key={level} value={level} className="text-xs">
                  {level}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>

        <ChipGroup
          label="Responsibilities"
          options={RESPONSIBILITIES}
          selected={areas}
          onToggle={toggleArea}
          hint="This will help Cai decide who to inform when a task or question is blocking its progress."
        />

        <Button size="xs" className="self-start">
          Send invite
        </Button>

        <p className="text-ink-400 border-ink-100 border-t pt-3 text-[10px] leading-snug">
          Invited people can see tasks, goals, and {agentName}&apos;s in-progress tasks. They cannot see your
          conversation with {agentName} &mdash; that&apos;s private to you.
        </p>
      </div>
    </div>
  )
}
